import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import AuthModal from '../../../shared/auth/AuthModal';
import { OrganicBlob, CirclePattern } from "../../../shared/decorations/OrganicBlob";
import { getDailyLeaderboard } from '../../../features/wordle/utils/scoreUtils';
import { getEasternDate } from '../../../features/wordle/utils/dailyWordUtils';

export function LeaderboardPreview() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);

  useEffect(() => {
    fetchTodayTopSolvers();
  }, []);

  // Navigate to leaderboard after successful auth
  useEffect(() => {
    if (pendingNavigation && currentUser) {
      navigate('/leaderboard');
      setPendingNavigation(false);
    }
  }, [currentUser, pendingNavigation, navigate]);

  const fetchTodayTopSolvers = async () => {
    try {
      const todayString = getEasternDate();
      const data = await getDailyLeaderboard(todayString, 5); // Get top 5 for today's daily word
      setLeaderboardData(data);
    } catch (error) {
      console.error('Error fetching today\'s top solvers:', error);
      setLeaderboardData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return (
    <section
      className="py-20 px-6 relative overflow-hidden"
      style={{ backgroundColor: '#151515', paddingTop: '100px', paddingBottom: '100px' }}
    >
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <OrganicBlob className="absolute w-96 h-96 top-10 left-10 animate-pulse" style={{ animationDelay: '0.3s' }} />
        <CirclePattern className="absolute w-64 h-64 bottom-10 right-20 opacity-40" />
      </div>

      <div className="max-w-[1200px] mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-10 items-center">
          {/* Left Column - Leaderboard */}
          <div className="lg:col-span-3">
            <h2
              className="mb-8"
              style={{
                color: '#FAFAF8',
                fontSize: '32px',
                fontWeight: '600'
              }}
            >
              Today's Top Solvers
            </h2>

            {loading ? (
              <div className="py-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#8B9D83' }}></div>
                <p style={{ color: '#E8DCC4', marginTop: '12px' }}>Loading...</p>
              </div>
            ) : leaderboardData.length === 0 ? (
              <div className="py-12 text-center">
                <p style={{ color: '#E8DCC4', fontSize: '18px' }}>
                  No games played yet today. Be the first!
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {leaderboardData.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="flex items-center py-4"
                    style={{
                      borderBottom: index < leaderboardData.length - 1 ? '1px solid #3A4238' : 'none',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '16px'
                    }}
                  >
                    <div className="flex items-center gap-6">
                      <span
                        style={{
                          color: '#8B9D83',
                          fontSize: '20px',
                          fontWeight: '700',
                          minWidth: '40px'
                        }}
                      >
                        {entry.rank}
                      </span>
                      <span style={{ color: '#FAFAF8', fontSize: '18px' }}>
                        {entry.username}
                      </span>
                    </div>
                    <span style={{ color: '#FAFAF8', fontSize: '18px', textAlign: 'center' }}>
                      {entry.attempts} {entry.attempts === 1 ? 'guess' : 'guesses'}
                    </span>
                    <span style={{ color: '#E8DCC4', fontSize: '16px', textAlign: 'right' }}>
                      {formatTime(entry.completionTimeMs)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - CTA */}
          <div className="lg:col-span-2 text-center flex flex-col items-center">
            <button
              onClick={() => {
                if (currentUser) {
                  navigate('/leaderboard');
                } else {
                  setShowAuthModal(true);
                  setPendingNavigation(true);
                }
              }}
              className="mb-6 px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:transform hover:scale-105"
              style={{
                backgroundColor: '#8B9D83',
                color: '#0A0A0A',
                fontSize: '20px',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                maxWidth: '280px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#9BAE92';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#8B9D83';
              }}
            >
              View Leaderboard
            </button>
            <p style={{ color: '#E8DCC4', fontSize: '18px', lineHeight: '1.6', textAlign: 'center' }}>
              Build your legacy. Connect with your community.
            </p>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="signin"
        />
      )}
    </section>
  );
}
